/**
 * "Continue with Google" — Google Identity Services, ID-token (credential) flow.
 *
 *   * The button is Google's own (`google.accounts.id.renderButton`), which is
 *     how the Sign in with Google branding guidelines are met: Google draws the
 *     logo, the "Continue with Google" text and the colours. Nothing here
 *     restyles it.
 *   * Popup UX, no One Tap: `prompt()` is never called, and `auto_select` is
 *     off, so nobody is signed in without clicking.
 *   * The credential goes straight to `POST /partner/auth/google`, which
 *     verifies it server-side (audience, issuer, expiry, email_verified). The
 *     portal never decodes or trusts it.
 *   * Configured by `VITE_GOOGLE_CLIENT_ID` (src/env.ts). Unset, nothing
 *     renders and the GIS script is never loaded, so the CSP allowance for
 *     accounts.google.com (public/_headers) is unused.
 */
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { GOOGLE_CLIENT_ID } from '../env';

/** The slice of the GIS API this file uses. */
export interface GoogleIdentity {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: { credential?: string }) => void;
        ux_mode?: 'popup' | 'redirect';
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
        context?: 'signin' | 'signup' | 'use';
        /** OIDC nonce Google signs into the ID token ("Link a Google account"). */
        nonce?: string;
      }) => void;
      renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
    };
  };
}

export const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

let pending: Promise<GoogleIdentity> | null = null;

/** Load the GIS client once per page. A failed load can be retried by a later mount. */
export function loadGoogleIdentity(): Promise<GoogleIdentity> {
  const existing = (window as unknown as { google?: GoogleIdentity }).google;
  if (existing?.accounts?.id) return Promise.resolve(existing);
  if (pending) return pending;
  pending = new Promise<GoogleIdentity>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      const g = (window as unknown as { google?: GoogleIdentity }).google;
      if (g?.accounts?.id) resolve(g);
      else reject(new Error('Google Identity Services did not initialise'));
    };
    script.onerror = () => reject(new Error('Google Identity Services failed to load'));
    document.head.appendChild(script);
  }).catch((err: unknown) => {
    pending = null;
    throw err;
  });
  return pending;
}

export interface GoogleSignInConfig {
  clientId: string;
  load: () => Promise<GoogleIdentity>;
}

const GoogleSignInContext = createContext<GoogleSignInConfig>({ clientId: GOOGLE_CLIENT_ID, load: loadGoogleIdentity });

/** Tests (and nothing else) override the client id and the script loader. */
export const GoogleSignInProvider = GoogleSignInContext.Provider;

export function useGoogleSignInConfig(): GoogleSignInConfig {
  return useContext(GoogleSignInContext);
}

function prefersDark(): boolean {
  const explicit = document.documentElement.dataset.theme;
  if (explicit) return explicit === 'dark';
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Google's button, or nothing when unconfigured. `onCredential` receives the
 * ID token. If the GIS script cannot load (an extension or network blocks
 * accounts.google.com), it says so and points at the email path instead.
 *
 * `nonce` (Your account → "Link a Google account"): the server's per-session
 * link nonce, which Google signs into the token so the link route can tell
 * the token was made for this session. `context: 'use'` then words Google's
 * popup as using an account rather than signing in.
 */
export function GoogleSignInButton({
  onCredential,
  nonce,
  failedText = 'Google sign-in couldn’t load in this browser. Use your work email below instead.',
}: {
  onCredential: (credential: string) => void;
  nonce?: string;
  failedText?: string;
}) {
  const { clientId, load } = useGoogleSignInConfig();
  const slot = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  // The latest handler, without re-rendering Google's button on every render.
  const handler = useRef(onCredential);
  handler.current = onCredential;

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    load()
      .then((google) => {
        const el = slot.current;
        if (cancelled || !el) return;
        google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response.credential) handler.current(response.credential);
          },
          ux_mode: 'popup',
          auto_select: false,
          cancel_on_tap_outside: true,
          context: nonce ? 'use' : 'signin',
          ...(nonce ? { nonce } : {}),
        });
        // GIS takes a pixel width (200–400); match the card so the button
        // lines up with the email field at every viewport.
        const width = Math.max(200, Math.min(400, Math.floor(el.getBoundingClientRect().width) || 400));
        el.replaceChildren();
        google.accounts.id.renderButton(el, {
          type: 'standard',
          theme: prefersDark() ? 'filled_black' : 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width,
          // The portal is English-only; without this GIS follows the browser or
          // IP locale and the one button on the page speaks another language.
          locale: 'en',
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, load, nonce]);

  if (!clientId) return null;
  if (failed) {
    return (
      <div className="notice warn" role="status">
        {failedText}
      </div>
    );
  }
  // Fixed height (Google's large button is 40 px) so nothing jumps when it paints.
  return <div ref={slot} className="google-signin" data-testid="google-signin" />;
}
